import Flex from '@Components/Bases/Flex';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import { useSaveNatureOfBusiness } from '@Domains/NatureOfBusiness';

import {
  deleteAssets,
  IUploadImage,
  useDeleteImage,
} from '@Domains/UploadImage';
import HomeIcon from '@mui/icons-material/Home';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, Button, Snackbar } from '@mui/material';
import { useNatureOfBusinessForm } from '@Router/NatureOfBusiness/Containers/NatureOfBusinessFormProvider';
import { mapNatureOfBusinessToFormValues } from '@Router/NatureOfBusiness/utils';
import { findUserRoles } from '@Utils/roleUtils';
import { updateCompletedSection } from '@Utils/updateCompletedSection';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';
import { SectionEnum } from 'src/Enum/SectionEnum';
import {
  BusinessGroupInfoFormValues,
  INatureOfBusinessFormValues,
} from '../NatureOfBusinessFormProvider/types';
import { mapNatureOfBussinessToPayload } from './utils';

export const footerHeight = 64;

const NatureOfBusinessFooter = () => {
  const { progressData, breadcrumbs, refetchProgressWithUpdate } =
    useProgressBar();
  const published = Boolean(progressData?.summarizeReport.publishedDate);
  const { getValues, trigger, setValue, reset } = useNatureOfBusinessForm();
  const navigate = useNavigate();
  const [{ loading }, executeSaveNatureOfBusiness] = useSaveNatureOfBusiness();
  const { id } = useParams();
  const [natureId, setNatureId] = useState<number | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [{ data: incomeMasterData }] = useGetInfoCategoryMasterData([
    INFO_CATEGORY_MASTER_DATA.PRODUCT_INCOME,
    INFO_CATEGORY_MASTER_DATA.EXPORT_INCOME,
    INFO_CATEGORY_MASTER_DATA.OTHER_INCOME,
  ]);
  const { user } = useProfile();
  const { deleteImage } = useDeleteImage(Number(id));

  const handleSave = async () => {
    const formValues = getValues();
    trigger();

    if (user) {
      const { cvupCreatorRole, cvupApproverRole } = findUserRoles(user.N_ROLES);

      try {
        setValue(
          'completedSection',
          updateCompletedSection(
            formValues.completedSection,
            cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
            cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
          )
        );
        [
          'corporateInformation',
          'productInformation',
          'marketingIndustry',
          'incomeStructure',
        ].forEach((key) => {
          if ((formValues as Record<string, any>)[key]) {
            setValue(
              `${key}.completedSection` as keyof INatureOfBusinessFormValues,
              updateCompletedSection(
                (formValues as Record<string, any>)[key].completedSection,
                cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
                cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
              )
            );
          }
        });

        const incomeStructureAssetsToDelete =
          formValues.incomeStructure?.assets?.filter(
            (asset: IUploadImage) => asset.markedForDeletion
          );

        const businessGroupInfosAssetsToDelete =
          formValues?.productInformation?.businessGroupInfos
            ?.flatMap(
              (groupInfo: BusinessGroupInfoFormValues) => groupInfo.assets ?? []
            )
            ?.filter(
              (asset: IUploadImage | undefined): asset is IUploadImage =>
                asset !== undefined && asset.markedForDeletion === true
            );

        if (incomeStructureAssetsToDelete?.length) {
          deleteAssets(
            'incomeStructure',
            incomeStructureAssetsToDelete,
            deleteImage
          );
        }

        if (businessGroupInfosAssetsToDelete?.length) {
          deleteAssets(
            'productInformationBusinessGroupInfos',
            businessGroupInfosAssetsToDelete,
            deleteImage
          );
        }

        const payload = mapNatureOfBussinessToPayload(
          formValues,
          incomeMasterData || []
        );

        const response = await executeSaveNatureOfBusiness({
          data: { id: natureId || formValues.id, ...payload },
          params: { reportId: id },
        });

        if (natureId === null) {
          setNatureId(response.data.id);
        }

        if (response) {
          const dataFormValues = mapNatureOfBusinessToFormValues(response.data);
          reset(
            { ...dataFormValues },
            { keepErrors: true, keepIsValidating: false }
          );

          setSnackbarMessage('บันทึกสำเร็จ');
          setSnackbarSeverity('success');
        }
      } catch (error) {
        console.error(error);
        setSnackbarMessage('เกิดข้อผิดพลาดในการบันทึก');
        setSnackbarSeverity('error');
      } finally {
        refetchProgressWithUpdate(true);
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handlePreviewClick = () => {
    if (id) {
      const url = `/report/${id}/preview?section=${SectionEnum.NatureOfBusiness}`;
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <Flex
        justifyContent={'space-between'}
        bgcolor={'common.white'}
        p={2}
        height={footerHeight}
        border={0}
        position="sticky"
        bottom={0}
        width="100%"
        zIndex={99}
        boxShadow={1}
      >
        <Button
          color="inherit"
          startIcon={<HomeIcon />}
          onClick={() => navigate(`/?report=${breadcrumbs?.reportId}`)}
        >
          กลับสู่หน้าหลัก
        </Button>
        <Flex gap={2}>
          <Button
            color="inherit"
            startIcon={<VisibilityIcon />}
            onClick={handlePreviewClick}
            disabled={published}
          >
            แสดงตัวอย่าง
          </Button>
          <Button
            type="submit"
            color="inherit"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            loading={loading}
            disabled={published}
          >
            บันทึก
          </Button>
        </Flex>
      </Flex>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '48px',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NatureOfBusinessFooter;
